"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { icon } from "@/components/ui/icons";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";

const ArrowBack = icon("ArrowBack");
const ArrowForward = icon("ArrowForward");

type ScrollableTab = {
  _key: string;
  triggerTitle: string;
};

export function ScrollableTabsList({ tabs }: { tabs: ScrollableTab[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.addEventListener("scroll", updateScrollState, { passive: true });

    // Re-measure on layout changes that don't fire window.resize: the container's
    // available width (e.g. the card-count indicators mounting into the header) and
    // the list's content width (e.g. web fonts loading and widening tab labels).
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    if (listRef.current) {
      observer.observe(listRef.current);
    }

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState]);

  const scrollByAmount = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.scrollBy({ left: direction * el.clientWidth * 0.6, behavior: "smooth" });
  };

  const hasOverflow = canScrollLeft || canScrollRight;

  return (
    <div className="gap-gap-element-sm flex min-w-0 flex-1 items-center">
      {hasOverflow && (
        <button
          type="button"
          aria-label="Scroll tabs left"
          onClick={() => scrollByAmount(-1)}
          disabled={!canScrollLeft}
          className="shrink-0 cursor-pointer rounded-full bg-white p-1.5 transition-opacity disabled:cursor-default disabled:opacity-30"
        >
          <ArrowBack className="text-neutral-darkest size-4" />
        </button>
      )}
      <div ref={scrollRef} className="scrollbar-hide min-w-0 flex-1 overflow-x-auto">
        <TabsList ref={listRef} className="typography-heading-4 gap-gap-element-xl w-max">
          {tabs.map((tab) => (
            <TabsTrigger key={tab._key} value={tab._key} className="shrink-0 whitespace-nowrap">
              {tab.triggerTitle}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
      {hasOverflow && (
        <button
          type="button"
          aria-label="Scroll tabs right"
          onClick={() => scrollByAmount(1)}
          disabled={!canScrollRight}
          className="shrink-0 cursor-pointer rounded-full bg-white p-1.5 transition-opacity disabled:cursor-default disabled:opacity-30"
        >
          <ArrowForward className="text-neutral-darkest size-4" />
        </button>
      )}
    </div>
  );
}
